import { tracked } from '@glimmer/tracking';

import type { Billing } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { getFormattedSyncInterval } from 'webapp/utils/number';

import type BillingPlan from 'webapp/models-custom/billing-plan';
import Serviced from 'webapp/models-custom/serviced';

export interface BasicFeatureInfo {
	name: string;
	formattedValue: string;
	id?: BillingFeature;
	description?: string;
	value?: number | boolean | null;
	current?: number;
}

export default class ActiveFeatures extends Serviced {
	@tracked featureData: Billing.FeatureInfo = {};
	@tracked plan: BillingPlan | null = null;

	// both featureData and plan can be empty while the app is still loading (initialRequests)

	get hasFeatureData(): boolean {
		// Active features referenced from billing service will have featureData
		// Active features on a plan (another plan) will have no featureData
		return Object.keys(this.featureData).length > 0;
	}

	get sourcesMax(): number | null {
		return this.hasFeatureData
			? this._getVal(this.featureData[BillingFeature.SOURCES]!.max)
			: this._getVal(this.plan?.featureData[BillingFeature.SOURCES]);
	}

	get advancedDataSourcesMax(): number | null {
		return this.advancedDataSourcesEnabled ? null : 0;
	}

	get sqlSourcesMax(): number | null {
		return this.sqlSourcesEnabled ? null : 0;
	}

	get sheetSourcesMax(): number | null {
		// all or nothing!
		return this.sqlSourcesEnabled ? null : 0;
	}

	get boardsMax(): number | null {
		return this.hasFeatureData
			? this._getVal(this.featureData[BillingFeature.BOARDS]!.max)
			: this._getVal(this.plan?.featureData[BillingFeature.BOARDS]);
	}

	get usersMax(): number | null {
		return this.hasFeatureData
			? this._getVal(this.featureData[BillingFeature.USERS]!.max)
			: this._getVal(this.plan?.featureData[BillingFeature.USERS]);
	}

	get clientsMax(): number | null {
		return this.hasFeatureData
			? this._getVal(this.featureData[BillingFeature.CLIENTS]!.max)
			: this._getVal(this.plan?.featureData[BillingFeature.CLIENTS]);
	}

	get customMetricsMax(): number | null {
		return this.hasFeatureData
			? this._getVal(this.featureData[BillingFeature.CUSTOM_METRICS]!.max)
			: this._getVal(this.plan?.featureData[BillingFeature.CUSTOM_METRICS]);
	}

	get storageMax(): number | null {
		return this.hasFeatureData
			? this._getVal(this.featureData[BillingFeature.STORAGE]!.max)
			: this._getVal(this.plan?.featureData[BillingFeature.STORAGE]);
	}

	get prioritySyncSourceCount(): number | null {
		return this.hasFeatureData
			? this._getVal(this.featureData[BillingFeature.PRIORITY_SYNC] as number)
			: this._getVal(this.plan?.featureData[BillingFeature.PRIORITY_SYNC]);
	}

	get qbEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.QB]
			: !!this.plan?.featureData[BillingFeature.QB];
	}

	get scriptsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.SCRIPTS]
			: !!this.plan?.featureData[BillingFeature.SCRIPTS];
	}

	get brandingEnabled(): boolean {
		// readonly legacy, used for pre-whitelabeling branding in webapp
		return this.hasFeatureData
			? !!this.featureData.branding
			: !!this.plan?.featureData.branding;
	}

	get ssoAllowed(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.SSO]
			: !!this.plan?.featureData[BillingFeature.SSO];
	}

	get customRangeEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.CUSTOM_RANGE]
			: !!this.plan?.featureData[BillingFeature.CUSTOM_RANGE];
	}

	get advancedReportsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.ADVANCED_REPORTS]
			: !!this.plan?.featureData[BillingFeature.ADVANCED_REPORTS];
	}

	get apiMetricBuilderEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.API_METRIC_BUILDER]
			: !!this.plan?.featureData[BillingFeature.API_METRIC_BUILDER];
	}

	get forecastsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.FORECASTS]
			: !!this.plan?.featureData[BillingFeature.FORECASTS];
	}

	get forecastModelingEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.FORECAST_MODELING]
			: !!this.plan?.featureData[BillingFeature.FORECAST_MODELING];
	}

	get generativeAIEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.GENERATIVE_AI]
			: !!this.plan?.featureData[BillingFeature.GENERATIVE_AI];
	}

	get historicalDataRetention(): number | null {
		return this.hasFeatureData
			? (this.featureData[BillingFeature.HISTORICAL_DATA] as number) || null
			: this.plan?.featureData[BillingFeature.HISTORICAL_DATA] || null;
	}

	get alertsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.ALERTS]
			: !!this.plan?.featureData[BillingFeature.ALERTS];
	}

	get goalsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.GOALS]
			: !!this.plan?.featureData[BillingFeature.GOALS];
	}

	get goalsOKRsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.OKR]
			: !!this.plan?.featureData[BillingFeature.OKR];
	}

	get mediaCreativesEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.MEDIA_CREATIVES]
			: !!this.plan?.featureData[BillingFeature.MEDIA_CREATIVES];
	}

	get reportsMax(): number | null {
		return this.hasFeatureData
			? this._getVal(this.featureData[BillingFeature.REPORTS_BASIC]!.max)
			: this._getVal(this.plan?.featureData[BillingFeature.REPORTS_BASIC]);
	}

	get dataPushEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.DATA_PUSH]
			: !!this.plan?.featureData[BillingFeature.DATA_PUSH];
	}

	get ingestionApiEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.INGESTION_API]
			: !!this.plan?.featureData[BillingFeature.INGESTION_API];
	}

	get workflowIntegrationsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.WORKFLOW_INTEGRATIONS]
			: !!this.plan?.featureData[BillingFeature.WORKFLOW_INTEGRATIONS];
	}

	get accountTemplatesEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.ACCOUNT_TEMPLATES]
			: !!this.plan?.featureData[BillingFeature.ACCOUNT_TEMPLATES];
	}

	get visualizationsAdvancedEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.VISUALIZATIONS_ADVANCED]
			: !!this.plan?.featureData[BillingFeature.VISUALIZATIONS_ADVANCED];
	}

	get ssoForced(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.SSO_FORCED]
			: !!this.plan?.featureData[BillingFeature.SSO_FORCED];
	}

	get mfaForced(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.MFA_FORCED]
			: !!this.plan?.featureData[BillingFeature.MFA_FORCED];
	}

	get mfaEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.MFA]
			: !!this.plan?.featureData[BillingFeature.MFA];
	}

	get anomalyEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.ANOMALY_DETECTION]
			: !!this.plan?.featureData[BillingFeature.ANOMALY_DETECTION];
	}

	get smartAlertsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.SMART_ALERTS]
			: !!this.plan?.featureData[BillingFeature.SMART_ALERTS];
	}

	get instantRefreshEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.INSTANT_REFRESH]
			: !!this.plan?.featureData[BillingFeature.INSTANT_REFRESH];
	}

	get goalsAdvancedEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.GOALS_ADVANCED]
			: !!this.plan?.featureData[BillingFeature.GOALS_ADVANCED];
	}

	get goalsForecastsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.GOALS_FORECASTS]
			: !!this.plan?.featureData[BillingFeature.GOALS_FORECASTS];
	}

	get boardsMultiplier(): number | null {
		// always read from plan, never customized
		return this.plan?.featureData.board_multiplier || null;
	}

	get dataRefreshInterval(): number {
		return this.hasFeatureData
			? (this.featureData[BillingFeature.DATA_REFRESH_INTERVAL] as number)
			: this.plan?.featureData[BillingFeature.DATA_REFRESH_INTERVAL] || 60;
	}

	get isLessThanDailyAndMoreThanHourly(): boolean {
		return this.dataRefreshInterval > 60 && this.dataRefreshInterval < 1440; // 24 * 60
	}

	get isDailyDataSync(): boolean {
		return this.dataRefreshInterval === 1440; // 24 * 60
	}

	get advancedDataSourcesEnabled(): boolean {
		// all-or-nothing source: active featureData stores this as { max } (null = enabled),
		// while plan featureData stores it as a truthy flag
		return this.hasFeatureData
			? this.featureData[BillingFeature.ADVANCED_DATA_SOURCES]!.max === null
			: !!this.plan?.featureData[BillingFeature.ADVANCED_DATA_SOURCES];
	}

	get sqlSourcesEnabled(): boolean {
		// max === null vs flag: see advancedDataSourcesEnabled
		return this.hasFeatureData
			? this.featureData[BillingFeature.SQL_SOURCES]!.max === null
			: !!this.plan?.featureData[BillingFeature.SQL_SOURCES];
	}

	get sheetSourcesEnabled(): boolean {
		// max === null vs flag: see advancedDataSourcesEnabled
		return this.hasFeatureData
			? this.featureData[BillingFeature.SHEET_SOURCES]!.max === null
			: !!this.plan?.featureData[BillingFeature.SHEET_SOURCES];
	}

	get sqlLegacyMetricsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.SQL_LEGACY_METRICS]
			: !!this.plan?.featureData[BillingFeature.SQL_LEGACY_METRICS];
	}

	get advancedDataSourcesLimitedEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.ADVANCED_DATA_SOURCES_LIMITED]
			: !!this.plan?.featureData[BillingFeature.ADVANCED_DATA_SOURCES_LIMITED];
	}

	get gsheetsLegacyMetricsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.GSHEETS_LEGACY_METRICS]
			: !!this.plan?.featureData[BillingFeature.GSHEETS_LEGACY_METRICS];
	}

	get aiChatAssistantEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.AI_CHAT_ASSISTANT]
			: !!this.plan?.featureData[BillingFeature.AI_CHAT_ASSISTANT];
	}

	get boardLoopsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.BOARD_LOOPS]
			: !!this.plan?.featureData[BillingFeature.BOARD_LOOPS];
	}

	get sharingEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.SHARING]
			: !!this.plan?.featureData[BillingFeature.SHARING];
	}

	get mcpEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.MCP]
			: !!this.plan?.featureData[BillingFeature.MCP];
	}

	get advancedDatasetFunctionalitiesEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.ADVANCED_DATASET_FUNCTIONALITIES]
			: !!this.plan?.featureData[BillingFeature.ADVANCED_DATASET_FUNCTIONALITIES];
	}

	get aiDataboardsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.AI_DATABOARDS]
			: !!this.plan?.featureData[BillingFeature.AI_DATABOARDS];
	}

	get premiumSourcesEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.PREMIUM_SOURCES]
			: !!this.plan?.featureData[BillingFeature.PREMIUM_SOURCES];
	}

	get aiCreditsMax(): number | null {
		return this.hasFeatureData
			? this._getVal(this.featureData[BillingFeature.AI_CREDITS]!.max)
			: this._getVal(this.plan?.featureData[BillingFeature.AI_CREDITS]);
	}

	get aiAgentsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.AI_AGENTS]
			: !!this.plan?.featureData[BillingFeature.AI_AGENTS];
	}

	get aiArtifactsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.AI_ARTIFACTS]
			: !!this.plan?.featureData[BillingFeature.AI_ARTIFACTS];
	}

	get aiMcpConnectorsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.AI_MCP_CONNECTORS]
			: !!this.plan?.featureData[BillingFeature.AI_MCP_CONNECTORS];
	}

	get aiRoutinesEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.AI_ROUTINES]
			: !!this.plan?.featureData[BillingFeature.AI_ROUTINES];
	}

	get aiSkillsCustomEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.AI_SKILLS_CUSTOM]
			: !!this.plan?.featureData[BillingFeature.AI_SKILLS_CUSTOM];
	}

	get aiSkillsMarketplaceEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.AI_SKILLS_MARKETPLACE]
			: !!this.plan?.featureData[BillingFeature.AI_SKILLS_MARKETPLACE];
	}

	get aiSlackAssistantEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.AI_SLACK_ASSISTANT]
			: !!this.plan?.featureData[BillingFeature.AI_SLACK_ASSISTANT];
	}

	get governanceVerificationEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.GOVERNANCE_VERIFICATION]
			: !!this.plan?.featureData[BillingFeature.GOVERNANCE_VERIFICATION];
	}

	get skipDwBrandingEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.SKIP_DW_BRANDING]
			: !!this.plan?.featureData[BillingFeature.SKIP_DW_BRANDING];
	}

	// ---------- ADDONS --------
	get whiteLabelingEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.WHITE_LABELING]
			: !!this.plan?.featureData[BillingFeature.WHITE_LABELING];
	}

	get prioritySupportEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.PRIORITY_SUPPORT]
			: !!this.plan?.featureData[BillingFeature.PRIORITY_SUPPORT];
	}

	get fiscalCalendarsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.FISCAL_CALENDARS]
			: !!this.plan?.featureData[BillingFeature.FISCAL_CALENDARS];
	}

	get activityLogEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.ACTIVITY_LOG]
			: !!this.plan?.featureData[BillingFeature.ACTIVITY_LOG];
	}

	get advancedSecurityEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.ADVANCED_SECURITY]
			: !!this.plan?.featureData[BillingFeature.ADVANCED_SECURITY];
	}

	get loginRecordsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.LOGIN_RECORDS]
			: !!this.plan?.featureData[BillingFeature.LOGIN_RECORDS];
	}

	get activeSessionsEnabled(): boolean {
		return this.hasFeatureData
			? !!this.featureData[BillingFeature.ACTIVE_SESSIONS]
			: !!this.plan?.featureData[BillingFeature.ACTIVE_SESSIONS];
	}

	// ---------- ADDONS --------

	_getVal(givenVal: number | null | undefined): number | null {
		// numerics, or null (unlimited)
		let val = null;
		if (typeof givenVal === 'number') {
			val = givenVal;
		}
		return val;
	}

	getValue(featureName: BillingFeature): number | boolean | null {
		switch (featureName) {
			case BillingFeature.BOARDS:
				return this.boardsMax;
			case BillingFeature.SOURCES:
				return this.sourcesMax;
			case BillingFeature.USERS:
				return this.usersMax;
			case BillingFeature.CLIENTS:
				return this.clientsMax;
			case BillingFeature.CUSTOM_METRICS:
				return this.customMetricsMax;
			case BillingFeature.STORAGE:
				return this.storageMax;
			case BillingFeature.QB:
				return this.qbEnabled;
			case BillingFeature.SCRIPTS:
				return this.scriptsEnabled;
			case BillingFeature.SSO:
				return this.ssoAllowed;
			case BillingFeature.HISTORICAL_DATA:
				return this.historicalDataRetention;
			case BillingFeature.DATA_REFRESH_INTERVAL:
				return this.dataRefreshInterval;
			case BillingFeature.CUSTOM_RANGE:
				return this.customRangeEnabled;
			case BillingFeature.ADVANCED_REPORTS:
				return this.advancedReportsEnabled;
			case BillingFeature.API_METRIC_BUILDER:
				return this.apiMetricBuilderEnabled;
			case BillingFeature.FORECASTS:
				return this.forecastsEnabled;
			case BillingFeature.FORECAST_MODELING:
				return this.forecastModelingEnabled;
			case BillingFeature.ALERTS:
				return this.alertsEnabled;
			case BillingFeature.GOALS:
				return this.goalsEnabled;
			case BillingFeature.REPORTS_BASIC:
				return this.reportsMax;
			case BillingFeature.DATA_PUSH:
				return this.dataPushEnabled;
			case BillingFeature.INGESTION_API:
				return this.ingestionApiEnabled;
			case BillingFeature.WORKFLOW_INTEGRATIONS:
				return this.workflowIntegrationsEnabled;
			case BillingFeature.ACCOUNT_TEMPLATES:
				return this.accountTemplatesEnabled;
			case BillingFeature.GENERATIVE_AI:
				return this.generativeAIEnabled;
			case BillingFeature.VISUALIZATIONS_ADVANCED:
				return this.visualizationsAdvancedEnabled;
			case BillingFeature.WHITE_LABELING:
				return this.whiteLabelingEnabled;
			case BillingFeature.PRIORITY_SUPPORT:
				return this.prioritySupportEnabled;
			case BillingFeature.FISCAL_CALENDARS:
				return this.fiscalCalendarsEnabled;
			case BillingFeature.PRIORITY_SYNC:
				return this.prioritySyncSourceCount;
			case BillingFeature.ACTIVITY_LOG:
				return this.activityLogEnabled;
			case BillingFeature.ADVANCED_SECURITY:
				return this.advancedSecurityEnabled;
			case BillingFeature.SSO_FORCED:
				return this.ssoForced;
			case BillingFeature.MFA_FORCED:
				return this.mfaForced;
			case BillingFeature.ANOMALY_DETECTION:
				return this.anomalyEnabled;
			case BillingFeature.SMART_ALERTS:
				return this.smartAlertsEnabled;
			case BillingFeature.INSTANT_REFRESH:
				return this.instantRefreshEnabled;
			case BillingFeature.MFA:
				return this.mfaEnabled;
			case BillingFeature.MEDIA_CREATIVES:
				return this.mediaCreativesEnabled;
			case BillingFeature.LOGIN_RECORDS:
				return this.loginRecordsEnabled;
			case BillingFeature.ACTIVE_SESSIONS:
				return this.activeSessionsEnabled;
			case BillingFeature.GOALS_ADVANCED:
				return this.goalsAdvancedEnabled;
			case BillingFeature.GOALS_FORECASTS:
				return this.goalsForecastsEnabled;
			case BillingFeature.ADVANCED_DATA_SOURCES:
				return this.advancedDataSourcesEnabled;
			case BillingFeature.SQL_SOURCES:
				return this.sqlSourcesEnabled;
			case BillingFeature.SHEET_SOURCES:
				return this.sheetSourcesEnabled;
			case BillingFeature.SQL_LEGACY_METRICS:
				return this.sqlLegacyMetricsEnabled;
			case BillingFeature.ADVANCED_DATA_SOURCES_LIMITED:
				return this.advancedDataSourcesLimitedEnabled;
			case BillingFeature.OKR:
				return this.goalsOKRsEnabled;
			case BillingFeature.BOARD_LOOPS:
				return this.boardLoopsEnabled;
			case BillingFeature.SHARING:
				return this.sharingEnabled;
			case BillingFeature.MCP:
				return this.mcpEnabled;
			case BillingFeature.ADVANCED_DATASET_FUNCTIONALITIES:
				return this.advancedDatasetFunctionalitiesEnabled;
			case BillingFeature.AI_CREDITS:
				return this.aiCreditsMax;
			case BillingFeature.AI_CHAT_ASSISTANT:
				return this.aiChatAssistantEnabled;
			case BillingFeature.GSHEETS_LEGACY_METRICS:
				return this.gsheetsLegacyMetricsEnabled;
			case BillingFeature.AI_DATABOARDS:
				return this.aiDataboardsEnabled;
			case BillingFeature.PREMIUM_SOURCES:
				return this.premiumSourcesEnabled;
			case BillingFeature.AI_AGENTS:
				return this.aiAgentsEnabled;
			case BillingFeature.AI_ARTIFACTS:
				return this.aiArtifactsEnabled;
			case BillingFeature.AI_MCP_CONNECTORS:
				return this.aiMcpConnectorsEnabled;
			case BillingFeature.AI_ROUTINES:
				return this.aiRoutinesEnabled;
			case BillingFeature.AI_SKILLS_CUSTOM:
				return this.aiSkillsCustomEnabled;
			case BillingFeature.AI_SKILLS_MARKETPLACE:
				return this.aiSkillsMarketplaceEnabled;
			case BillingFeature.AI_SLACK_ASSISTANT:
				return this.aiSlackAssistantEnabled;
			case BillingFeature.GOVERNANCE_VERIFICATION:
				return this.governanceVerificationEnabled;
			case BillingFeature.SKIP_DW_BRANDING:
				return this.skipDwBrandingEnabled;
			default: {
				const _exhaustive: never = featureName;
				return _exhaustive;
			}
		}
	}

	isCountable(featureName: BillingFeature): boolean {
		return [
			BillingFeature.BOARDS,
			BillingFeature.SOURCES,
			BillingFeature.USERS,
			BillingFeature.CLIENTS,
			BillingFeature.CUSTOM_METRICS,
			BillingFeature.STORAGE,
			BillingFeature.HISTORICAL_DATA,
			BillingFeature.AI_CREDITS,
			BillingFeature.REPORTS_BASIC,
		].includes(featureName);
	}

	getFeatureInfo(featureName: BillingFeature): BasicFeatureInfo | null {
		let info: BasicFeatureInfo | null = null;

		const staticInfo = ActiveFeatures.featureInfos[featureName];

		if (staticInfo) {
			const value = this.getValue(featureName);
			info = {
				id: featureName,
				name: staticInfo.name,
				description: staticInfo.description,
				value,
				formattedValue: this.formatValue(featureName, value),
			};
		}

		return info;
	}

	updateFromPlan(plan: BillingPlan): void {
		this.plan = plan;
	}

	updateFeatureData(featureData: Billing.FeatureInfo): void {
		this.featureData = featureData;
	}

	formatHistoricalDataValue(value: number | boolean | null, unlimitedAsInfinity = false) {
		if (value) {
			return `${value} months`;
		} else {
			return unlimitedAsInfinity ? '∞' : 'Unlimited';
		}
	}

	private formatValue(featureName: BillingFeature, value: number | boolean | null) {
		if (featureName === BillingFeature.HISTORICAL_DATA) {
			return this.formatHistoricalDataValue(value);
		} else if (featureName === BillingFeature.DATA_REFRESH_INTERVAL) {
			return getFormattedSyncInterval(this.dataRefreshInterval);
		}
		return '';
	}

	static compareFeatureValues(
		featureName: string,
		value1: number | null,
		value2: number | null,
	): boolean {
		let isValue2Better = false;

		if (featureName === BillingFeature.DATA_REFRESH_INTERVAL) {
			isValue2Better = value2! < value1!; // less is better!
		} else {
			isValue2Better = value1 !== null && value2! > value1;

			if (
				featureName === BillingFeature.HISTORICAL_DATA &&
				value1 !== null &&
				value2 === null
			) {
				isValue2Better = true;
			}
		}

		return isValue2Better;
	}

	static featureInfos: Record<BillingFeature, { name: string; description: string }> = {
		[BillingFeature.BOARDS]: {
			name: 'Dashboards',
			description: '',
		},
		[BillingFeature.SOURCES]: {
			name: 'Data sources',
			description: '',
		},
		[BillingFeature.USERS]: {
			name: 'Users',
			description: '',
		},
		[BillingFeature.CLIENTS]: {
			name: 'Accounts',
			description: '',
		},
		[BillingFeature.CUSTOM_METRICS]: {
			name: 'Custom metrics',
			description: '',
		},
		[BillingFeature.QB]: {
			name: 'Metric builder',
			description: '',
		},
		[BillingFeature.SCRIPTS]: {
			name: 'Data calculations',
			description: '',
		},
		[BillingFeature.HISTORICAL_DATA]: {
			name: 'Historical data',
			description: '',
		},
		[BillingFeature.DATA_REFRESH_INTERVAL]: {
			name: 'Data sync frequency',
			description: '',
		},
		[BillingFeature.SSO]: {
			name: 'SSO',
			description: '',
		},
		[BillingFeature.CUSTOM_RANGE]: {
			name: 'Custom date range',
			description: '',
		},
		[BillingFeature.STORAGE]: {
			name: 'Data storage',
			description: '',
		},
		[BillingFeature.PREMIUM_SOURCES]: {
			name: 'Premium data sources',
			description: '',
		},
		[BillingFeature.ADVANCED_REPORTS]: {
			name: 'Advanced reporting',
			description: '',
		},
		[BillingFeature.API_METRIC_BUILDER]: {
			name: 'Custom API integrations',
			description: '',
		},
		[BillingFeature.FORECASTS]: {
			name: 'Forecasts',
			description: '',
		},
		[BillingFeature.FORECAST_MODELING]: {
			name: 'Forecast modeling',
			description: '',
		},
		[BillingFeature.ALERTS]: {
			name: 'Alerts',
			description: '',
		},
		[BillingFeature.GOALS]: {
			name: 'Goals',
			description: '',
		},
		[BillingFeature.GOALS_ADVANCED]: {
			name: 'Advanced goals',
			description: '',
		},
		[BillingFeature.GOALS_FORECASTS]: {
			name: 'Forecasts',
			description: '',
		},
		[BillingFeature.REPORTS_BASIC]: {
			name: 'Reports',
			description: '',
		},
		[BillingFeature.DATA_PUSH]: {
			name: 'Data push',
			description: '',
		},
		[BillingFeature.INGESTION_API]: {
			name: 'Databox API',
			description: '',
		},
		[BillingFeature.WORKFLOW_INTEGRATIONS]: {
			name: 'Workflow integrations',
			description: '',
		},
		[BillingFeature.ACCOUNT_TEMPLATES]: {
			name: 'Account templates',
			description: '',
		},
		[BillingFeature.GENERATIVE_AI]: {
			name: 'Databox AI',
			description: '',
		},
		[BillingFeature.VISUALIZATIONS_ADVANCED]: {
			name: 'Adv. visualizations',
			description: '',
		},

		[BillingFeature.ACTIVITY_LOG]: {
			name: 'Activity log',
			description: '',
		},
		[BillingFeature.SSO_FORCED]: {
			name: 'Force SSO',
			description: '',
		},
		[BillingFeature.MFA_FORCED]: {
			name: 'Force MFA',
			description: '',
		},
		[BillingFeature.MFA]: {
			name: 'Two-factor authentication (2FA)',
			description: '',
		},
		[BillingFeature.ANOMALY_DETECTION]: {
			name: 'Anomaly detection',
			description: '',
		},
		[BillingFeature.SMART_ALERTS]: {
			name: 'Smart alerts',
			description: '',
		},
		[BillingFeature.LOGIN_RECORDS]: {
			name: 'Login records',
			description: '',
		},
		[BillingFeature.ACTIVE_SESSIONS]: {
			name: 'Active sessions',
			description: '',
		},
		[BillingFeature.MEDIA_CREATIVES]: {
			name: 'Media creatives',
			description: '',
		},
		[BillingFeature.INSTANT_REFRESH]: {
			name: 'Instant refresh',
			description: '',
		},
		[BillingFeature.BOARD_LOOPS]: {
			name: 'Looped dashboards',
			description: '',
		},
		[BillingFeature.SHARING]: {
			name: 'Sharing & notifications',
			description: '',
		},

		// Addon as features
		[BillingFeature.WHITE_LABELING]: {
			name: 'Branding & white-labeling',
			description:
				'Remove Databox branding and replace it with your own logo, colors, and domain so clients experience a fully branded reporting environment. Ideal for agencies and resellers who want to offer analytics as part of their own product or service.',
		},
		[BillingFeature.PRIORITY_SUPPORT]: {
			name: 'Priority support',
			description:
				'Chat and email support cases are routed to the top of the queue and handled with highest priority.',
		},
		[BillingFeature.PRIORITY_SYNC]: {
			name: '15 min sync per source',
			description:
				'Keep data refreshed every 15 minutes for selected data sources, giving teams access to near real-time performance data throughout the day. Availability varies by data source based on vendor API capabilities and rate limits.',
		},
		[BillingFeature.FISCAL_CALENDARS]: {
			name: 'Fiscal calendar',
			description:
				'Align all date ranges and reporting periods to your financial year instead of the standard calendar, so dashboards and metrics reflect how your business actually measures and tracks performance over time.',
		},
		[BillingFeature.ADVANCED_SECURITY]: {
			name: 'Advanced security management',
			description:
				'Enforce SSO and two-factor authentication, oversee account access, and control active sessions across the account.',
		},
		[BillingFeature.OKR]: {
			name: 'OKRs',
			description:
				'Define objectives, break them down into key results and sub-goals, and connect them directly to performance data in Databox. Assign ownership to teams or individuals, track progress automatically, and keep every level of the organization aligned around the work that drives results.',
		},

		[BillingFeature.AI_DATABOARDS]: {
			name: 'AI dashboards',
			description: 'Generate dashboards with AI',
		},

		[BillingFeature.ADVANCED_DATA_SOURCES]: {
			name: 'Datasets',
			description:
				'Create and hand-pick properties from data sources with replicated data, that will allow you to view, filter, export and merge raw data.',
		},

		[BillingFeature.SQL_SOURCES]: {
			name: 'SQL sources',
			description: '',
		},
		[BillingFeature.SQL_LEGACY_METRICS]: {
			name: 'SQL legacy metrics',
			description: '',
		},
		[BillingFeature.GSHEETS_LEGACY_METRICS]: {
			name: 'Google Sheets legacy metrics',
			description: '',
		},
		[BillingFeature.ADVANCED_DATASET_FUNCTIONALITIES]: {
			name: 'Advanced dataset functionalities',
			description: '',
		},
		[BillingFeature.ADVANCED_DATA_SOURCES_LIMITED]: {
			name: 'Advanced data sources limited',
			description: '',
		},
		[BillingFeature.AI_CHAT_ASSISTANT]: {
			name: 'AI chat assistant',
			description: '',
		},
		[BillingFeature.MCP]: {
			name: 'MCP server',
			description: '',
		},
		[BillingFeature.SHEET_SOURCES]: {
			name: 'Spreadsheets',
			description: '',
		},
		[BillingFeature.AI_CREDITS]: {
			name: 'AI credits',
			description: '',
		},
		[BillingFeature.AI_AGENTS]: {
			name: 'AI agents',
			description: '',
		},
		[BillingFeature.AI_ARTIFACTS]: {
			name: 'AI artifacts',
			description: '',
		},
		[BillingFeature.AI_MCP_CONNECTORS]: {
			name: 'AI MCP connectors',
			description: '',
		},
		[BillingFeature.AI_ROUTINES]: {
			name: 'AI routines',
			description: '',
		},
		[BillingFeature.AI_SKILLS_CUSTOM]: {
			name: 'Custom skills',
			description: '',
		},
		[BillingFeature.AI_SKILLS_MARKETPLACE]: {
			name: 'Marketplace skills',
			description: '',
		},
		[BillingFeature.AI_SLACK_ASSISTANT]: {
			name: 'AI Slack assistant',
			description: '',
		},
		[BillingFeature.GOVERNANCE_VERIFICATION]: {
			name: 'Governance verification',
			description: '',
		},
		[BillingFeature.SKIP_DW_BRANDING]: {
			name: 'Skip Databox branding',
			description: '',
		},
	};
}
